//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { DEFAULT_CHART_OPTIONS } from '@facebook/constants/facebookChartConstants';
import { SocialNetwork } from '@models/SocialNetwork';
import { getReachTooltipContentCaveat } from '@utils/social-text-utils/socialTextUtils';

export enum FacebookPageEngagementChartOptions {
  LINK_CLICKS = 'visits',
  REACH = 'reach',
  REVENUE = 'revenue',
}

export const FACEBOOK_ENGAGEMENT_CHART_LABELS = {
  [FacebookPageEngagementChartOptions.LINK_CLICKS]: 'Total Link Clicks',
  [FacebookPageEngagementChartOptions.REACH]: 'Total Reach',
  [FacebookPageEngagementChartOptions.REVENUE]: 'Total Revenue',
};

export const FACEBOOK_ENGAGEMENT_CHART_OPTIONS = {
  [FacebookPageEngagementChartOptions.LINK_CLICKS]: DEFAULT_CHART_OPTIONS,
  [FacebookPageEngagementChartOptions.REACH]: DEFAULT_CHART_OPTIONS,
  [FacebookPageEngagementChartOptions.REVENUE]: DEFAULT_CHART_OPTIONS,
};

export enum FacebookPageEngagementSortKey {
  CTR = 'CTR',
  COMMENTS = 'Comments',
  ENGAGEMENT_RATE = 'EngagementRate',
  IMPRESSIONS = 'Impressions',
  INTERACTIONS = 'Interactions',
  PUBLISH_DATE = 'PublishDate',
  REACH = 'Reach',
  REACTIONS = 'Reactions',
  SHARES = 'Shares',
  SITE_VISITS = 'SiteVisits',
  REVENUE = 'Revenue',
}

export const FACEBOOK_ENGAGEMENT_POST_HEADERS: Partial<
  Record<FacebookPageEngagementSortKey, string>
> = {
  [FacebookPageEngagementSortKey.CTR]: 'CTR',
  [FacebookPageEngagementSortKey.ENGAGEMENT_RATE]: 'Eng. Rate',
  [FacebookPageEngagementSortKey.IMPRESSIONS]: 'Views',
  [FacebookPageEngagementSortKey.INTERACTIONS]: 'Interactions',
  [FacebookPageEngagementSortKey.PUBLISH_DATE]: 'Publish Date',
  [FacebookPageEngagementSortKey.REACH]: 'Reach',
  [FacebookPageEngagementSortKey.SITE_VISITS]: 'Link Clicks',
  [FacebookPageEngagementSortKey.REVENUE]: 'Revenue',
};

export const FACEBOOK_ENGAGEMENT_POST_SORT_OPTIONS = {
  [FacebookPageEngagementSortKey.COMMENTS]: 'Comments',
  [FacebookPageEngagementSortKey.CTR]: 'Clickthrough Rate',
  [FacebookPageEngagementSortKey.ENGAGEMENT_RATE]: 'Engagement Rate',
  [FacebookPageEngagementSortKey.IMPRESSIONS]: 'Views',
  [FacebookPageEngagementSortKey.INTERACTIONS]: 'Interactions',
  [FacebookPageEngagementSortKey.PUBLISH_DATE]: 'Publish Date',
  [FacebookPageEngagementSortKey.REACH]: 'Reach',
  [FacebookPageEngagementSortKey.REACTIONS]: 'Reactions',
  [FacebookPageEngagementSortKey.SHARES]: 'Shares',
  [FacebookPageEngagementSortKey.SITE_VISITS]: 'Link Clicks',
  [FacebookPageEngagementSortKey.REVENUE]: 'Revenue',
};

export const FACEBOOK_ENGAGEMENT_POST_SORT_TOOLTIPS: Partial<
  Record<FacebookPageEngagementSortKey, string>
> = {
  [FacebookPageEngagementSortKey.CTR]: `Clickthrough rate is the total link clicks for this post divided by the post's impressions.`,
  [FacebookPageEngagementSortKey.ENGAGEMENT_RATE]: `Total reactions, comments, and shares divided by reach for this post.`,
  [FacebookPageEngagementSortKey.IMPRESSIONS]: `Number of times this post has been viewed.`,
  [FacebookPageEngagementSortKey.INTERACTIONS]: `Reactions, comments, and shares for this post.`,
  [FacebookPageEngagementSortKey.REACH]: `Number of unique users who have seen this post. ${getReachTooltipContentCaveat(
    SocialNetwork.Facebook,
  )}`,
  [FacebookPageEngagementSortKey.SITE_VISITS]: `Number of clicks on any links appearing in this post.`,
  [FacebookPageEngagementSortKey.REVENUE]:
    'Total revenue from purchases made through links embedded in this post.',
};

export enum FacebookPageEngagementSortDir {
  ASC = 'Asc',
  DESC = 'Desc',
}

export const FacebookEngagementStatBubbleTitle = {
  AverageReach: 'Average Reach',
  Revenue: 'Revenue',
  TotalReactions: 'Total Reactions',
  TotalComments: 'Total Comments',
  TotalShares: 'Total Shares',
  TotalLinkClicks: 'Total Link Clicks',
};

export const FacebookEngagementStatBubbleTooltips = {
  AverageReach: `The average number of unique users who viewed your posts over the selected time period. ${getReachTooltipContentCaveat(
    SocialNetwork.Facebook,
  )}`,
  Revenue:
    'The total revenue attributed to links in your posts over the selected time period.',
  TotalReactions:
    'The total number of reactions to your posts over the selected time period.',
  TotalComments:
    'The total number of comments received on your posts over the selected time period.',
  TotalShares:
    'The number of times your posts were shared over the selected time period.',
  TotalLinkClicks:
    'The number of times links on your posts were clicked over the selected time period.',
};

export const FACEBOOK_POST_ROW_IMAGE_DEFAULT_SIZE = 100;
